import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  DirectionsCar as CarIcon,
  Timer as TimerIcon,
  Notes as NotesIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useLeadStore } from '../stores/leadStore';
import { useAuthStore } from '../stores/authStore';

// Using Lead type from the store

const stageColumns = [
  { id: 'assigned', title: 'Assigned', color: '#2196f3' },
  { id: 'contacted', title: 'Contacted', color: '#ff9800' },
  { id: 'appointment', title: 'Appointment Made', color: '#4caf50' },
  { id: 'no-interest', title: 'No Interest', color: '#9e9e9e' },
];

const getInterestColor = (level: string) => {
  switch (level) {
    case 'high':
      return '#4caf50';
    case 'medium':
      return '#ff9800';
    case 'low':
      return '#f44336';
    case 'ready_to_buy':
      return '#00897B';
    default:
      return '#9e9e9e';
  }
};

const getInterestLabel = (level: string) => {
  switch ((level || '').toLowerCase()) {
    case 'high':
      return 'High Interest';
    case 'medium':
      return 'Interested';
    case 'low':
      return 'Low Interest';
    case 'ready_to_buy':
      return 'Ready to Buy';
    case 'unknown':
    default:
      return 'New';
  }
};

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case 'whatsapp': return <WhatsAppIcon sx={{ fontSize: 16 }} />;
    case 'sms': return <MessageIcon sx={{ fontSize: 16 }} />;
    case 'phone': return <PhoneIcon sx={{ fontSize: 16 }} />;
    default: return <MessageIcon sx={{ fontSize: 16 }} />;
  }
};

const MyQueue: React.FC = () => {
  const { user } = useAuthStore();
  const { myLeads, updateLeadStage, closeLead, initializeLeads } = useLeadStore();
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  useEffect(() => {
    initializeLeads();
  }, [initializeLeads]);

  const handleDragStart = (e: React.DragEvent, lead: any) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ id: lead.id, stage: lead.stage }));
    setDraggedLeadId(lead.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, newStage: string) => {
    e.preventDefault();

    const data = e.dataTransfer.getData('application/json');
    if (data) {
      const leadData = JSON.parse(data);
      if (leadData.stage !== newStage) {
        updateLeadStage(leadData.id, newStage as any);
      }
    }

    setDraggedLeadId(null);
    setDragOverColumn(null);
  };

  const handleLeadClick = (lead: any) => {
    setSelectedLead(lead);
    setDialogOpen(true);
    setTabValue(0);
    setNewNote('');
  };

  const handleSaveNotes = () => {
    if (selectedLead && newNote) {
      // In production, this would update the lead notes via the store
      setNewNote('');
    }
  };

  const handleCloseLead = (leadId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the dialog
    closeLead(leadId);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    }
  };

  const LeadCard = ({ lead }: { lead: any }) => (
    <Card
      sx={{
        mb: 2,
        cursor: 'grab',
        opacity: draggedLeadId === lead.id ? 0.5 : 1,
        '&:hover': {
          boxShadow: 3,
        },
        position: 'relative',
        userSelect: 'none',
      }}
      draggable
      onDragStart={(e) => handleDragStart(e, lead)}
      onDragEnd={handleDragEnd}
      onClick={() => handleLeadClick(lead)}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Close button for No Interest leads */}
        {lead.stage === 'no-interest' && (
          <IconButton
            size="small"
            onClick={(e) => handleCloseLead(lead.id, e)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'error.light',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.main',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a237e', fontSize: '0.9rem' }}>
              {lead.customerName.charAt(0)}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">
              {lead.customerName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: lead.stage === 'no-interest' ? 3 : 0 }}>
            {getChannelIcon(lead.channel)}
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(lead.lastContact)}
            </Typography>
          </Box>
        </Box>

        {/* Vehicle Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <CarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {lead.vehicleYear} {lead.vehicle}
          </Typography>
        </Box>

        {/* Scenario & Interest */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Chip
            label={lead.scenario}
            size="small"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
          <Chip
            label={getInterestLabel(lead.interestLevel)}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              bgcolor: getInterestColor(lead.interestLevel),
              color: 'white',
            }}
          />
        </Box>

        {/* AI Summary Preview */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {lead.aiSummary}
        </Typography>

        {/* Handoff Reason */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <PsychologyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography variant="caption" color="primary.main" fontWeight="medium">
            {lead.handoffReason}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        AI handover leads - drag cards to update status
      </Typography>

      {/* Pipeline Columns */}
      <Grid container spacing={2}>
        {stageColumns.map((column) => {
          const columnLeads = myLeads.filter(lead => lead.stage === column.id);
          return (
            <Grid item xs={12} sm={6} md={3} key={column.id}>
              <Paper
                sx={{
                  p: 2,
                  minHeight: 600,
                  bgcolor: dragOverColumn === column.id ? '#e3f2fd' : '#f5f5f5',
                  borderTop: `3px solid ${column.color}`,
                  boxShadow: dragOverColumn === column.id ? 4 : 1,
                  border: dragOverColumn === column.id ? '2px dashed' : 'none',
                  borderColor: dragOverColumn === column.id ? column.color : 'transparent',
                }}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {column.title}
                  </Typography>
                  <Chip label={columnLeads.length} size="small" />
                </Box>

                <Box sx={{ minHeight: 500, position: 'relative' }}>
                  {columnLeads.map((lead) => (
                    <LeadCard key={lead.id} lead={lead} />
                  ))}
                  {/* Visual indicator for empty columns */}
                  {columnLeads.length === 0 && draggedLeadId && (
                    <Box sx={{
                      height: '100%',
                      minHeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: dragOverColumn === column.id ? column.color : '#ccc',
                      borderRadius: 1,
                      backgroundColor: dragOverColumn === column.id ? `${column.color}15` : 'transparent',
                    }}>
                      <Typography color="text.secondary" variant="h6">
                        Drop here
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Lead Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedLead && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#1a237e' }}>
                    {selectedLead.customerName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedLead.customerName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLead.vehicleYear} {selectedLead.vehicle} • {selectedLead.phone}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="AI Chat History" />
                <Tab label="Summary & Notes" />
                <Tab label="Contact Actions" />
              </Tabs>

              {/* AI Chat History Tab */}
              {tabValue === 0 && (
                <Box>
                  <Paper sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PsychologyIcon color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI Handoff Reason
                      </Typography>
                    </Box>
                    <Typography variant="body2">{selectedLead.handoffReason}</Typography>
                  </Paper>

                  <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {selectedLead.chatHistory.map((message: any) => (
                      <ListItem
                        key={message.id}
                        sx={{
                          flexDirection: message.sender === 'bot' ? 'row' : 'row-reverse',
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: message.sender === 'bot' ? '#1a237e' : '#4caf50',
                              width: 32,
                              height: 32,
                            }}
                          >
                            {message.sender === 'bot' ? 'AI' : 'C'}
                          </Avatar>
                        </ListItemAvatar>
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: message.sender === 'bot' ? '#e3f2fd' : '#f1f8e9',
                            maxWidth: '70%',
                          }}
                        >
                          <Typography variant="body2">{message.message}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {message.timestamp}
                          </Typography>
                        </Paper>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Summary & Notes Tab */}
              {tabValue === 1 && (
                <Box>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      AI Summary
                    </Typography>
                    <Typography variant="body2">{selectedLead.aiSummary}</Typography>
                  </Paper>

                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Agent Notes
                    </Typography>
                    {selectedLead.notes ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedLead.notes}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No notes yet
                      </Typography>
                    )}
                  </Paper>

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<NotesIcon />}
                      onClick={handleSaveNotes}
                      disabled={!newNote}
                    >
                      Add Note
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Contact Actions Tab */}
              {tabValue === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhoneIcon />}
                    size="large"
                    fullWidth
                  >
                    Call {selectedLead.phone}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<WhatsAppIcon />}
                    size="large"
                    fullWidth
                  >
                    Send WhatsApp Message
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<MessageIcon />}
                    size="large"
                    fullWidth
                  >
                    Send SMS
                  </Button>
                  <Divider />
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                  >
                    Schedule Follow-up
                  </Button>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              {selectedLead.stage === 'no-interest' && (
                <Button
                  color="error"
                  startIcon={<ArchiveIcon />}
                  onClick={() => {
                    handleCloseLead(selectedLead.id, { stopPropagation: () => {} } as any);
                    setDialogOpen(false);
                  }}
                  sx={{ mr: 'auto' }}
                >
                  Archive Lead
                </Button>
              )}
              <Button onClick={() => setDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MyQueue;
